import { put, race, select, take, fork, call, all } from "redux-saga/effects";

import * as actionTypes from "./actionTypes";
import * as attachmentActionTypes from "../attachments/actionTypes";
import * as selectors from "./selectors";
import {
  addActionRequestChain,
  loadActionRequestChain,
  loadRulesRequestChain,
  saveActionRequestChain,
  attachmentUploadFail,
  attachmentUploadSuccess,
  loadCommentsRequestChain,
} from "./actionCreators";
import {
  serialiseCreateAction,
  serialiseEditAction,
} from "./utils/serialiseAction";
import { getSignedUrl, uploadToS3 } from "../attachments/actionCreators";
import { formatEquipList } from "./utils/formatEquipList";
import { unassociateEquipRuleFlow } from "../_BETA/unassociate";
import { history } from "../../../browserHistory";
import {
  addedActionEvent,
  savedActionEvent,
} from "../../../utils/eventTracking";
import { getUsernameList } from "../issues";
import {
  postAssociateEquipRule,
  ASSOCIATE_RULE_EQUIP_REQUEST_SUCCESS,
} from "../_BETA/associate";

/**
 * Saga triggered when loading in an existing action to be edited
 */
export function* loadRulesFlow() {
  while (true) {
    yield take(actionTypes.LOAD_RULES);

    const token = yield select(selectors.getUserToken);
    yield put(loadRulesRequestChain(token));

    const { failure } = yield race({
      success: take(actionTypes.LOAD_RULES_REQUEST_SUCCESS),
      failure: take(actionTypes.LOAD_RULES_REQUEST_FAIL),
    });

    // TODO: Redirect to 404/error?
    if (failure) {
      throw Error(failure.error.message);
    }
  }
}

/**
 * Saga triggered by `LOAD_ACTION` that _only_ loads only the action data
 */
export function* loadActionFlow() {
  while (true) {
    const { actionId } = yield take(actionTypes.LOAD_ACTION);
    const token = yield select(selectors.getUserToken);

    // Dispatch all requests at the same time
    yield put(loadActionRequestChain(actionId, token));
    yield put(loadCommentsRequestChain(actionId, token));

    // Unblock each step by racing them all
    const [
      { success: action, failure: actionFailure },
      { success: comments, failure: commentsFailure },
    ] = yield all([
      race({
        success: take(actionTypes.LOAD_ACTION_REQUEST_SUCCESS),
        failure: take(actionTypes.LOAD_ACTION_REQUEST_FAIL),
      }),
      race({
        success: take(actionTypes.LOAD_COMMENTS_REQUEST_SUCCESS),
        failure: take(actionTypes.LOAD_COMMENTS_REQUEST_FAIL),
      }),
    ]);

    try {
      if (actionFailure) throw Error(actionFailure.error.message);
      if (commentsFailure) throw Error(commentsFailure.error.message);
    } catch (error) {
      console.error(error);
    }
    // TODO: Toastr error handling?

    if (action && comments) {
      // Grab the username list for the add comment tagging
      yield put(getUsernameList({ token }, action.result));
    }
  }
}

/**
 * Saga triggered by `LOAD_ACTION_FOR_EDITING` for loading both the action data
 * and the rule data
 */
export function* loadInitialActionFlow() {
  while (true) {
    // take the initial action + setup
    const { actionId } = yield take(actionTypes.LOAD_ACTION_FOR_EDITING);
    const token = yield select(selectors.getUserToken);

    // load the initial action data
    yield put(loadActionRequestChain(actionId, token));
    const { failure } = yield race({
      success: take(actionTypes.LOAD_ACTION_REQUEST_SUCCESS),
      failure: take(actionTypes.LOAD_ACTION_REQUEST_FAIL),
    });

    if (failure) {
      throw Error(failure.error.message);
    }

    yield put(loadRulesRequestChain(token));
    const { rulesLoadFail } = yield race({
      success: take(actionTypes.LOAD_RULES_REQUEST_SUCCESS),
      rulesLoadFail: take(actionTypes.LOAD_RULES_REQUEST_FAIL),
    });

    if (rulesLoadFail) {
      throw Error(rulesLoadFail.error.message);
    }
  }
}

/**
 * Upload and retreive urls for an image. Only exposed for tests, this generator
 * is used internally.
 * @param {string} id
 * @param {File} file
 */
export function* uploadScreenshot(id, file) {
  const token = yield select(selectors.getUserToken);

  const { name, type } = file;

  // get the signed urls
  // NOTE: The auth on this endpoint is non-existent in captain
  yield put(getSignedUrl({ token }, id, name, type));
  const { signSuccess, signFailure } = yield race({
    signSuccess: take(attachmentActionTypes.GET_SIGNED_URL_SUCCESS),
    signFailure: take(attachmentActionTypes.GET_SIGNED_URL_FAIL),
  });

  if (signFailure) {
    yield put(
      attachmentUploadFail("Unable to get signed URL for attachment upload")
    );
  }

  // upload the image using the put url from signing
  yield put(uploadToS3(signSuccess.result.signedPutUrl, file));
  const { s3Success, s3Failure } = yield race({
    s3Success: take(attachmentActionTypes.UPLOAD_TO_S3_SUCCESS),
    s3Failure: take(attachmentActionTypes.UPLOAD_TO_S3_FAIL),
  });

  if (s3Failure) {
    yield put(attachmentUploadFail("Unable to upload attachment"));
  }

  if (s3Success) {
    yield put(attachmentUploadSuccess(signSuccess.result.publicReadUrl));
  }
}

/**
 * Save/updated an action to Jira and dispatch errors if necessary and redirect.
 * Only exposed for tests, this generator is used internally.
 * @param {Object} action A serialised action object
 */
export function* saveAction(action) {
  const token = yield select(selectors.getUserToken);
  const actionId = yield select(selectors.getActionId);

  yield put(saveActionRequestChain(actionId, action, token));
  const { success } = yield race({
    success: take(actionTypes.SAVE_ACTION_REQUEST_SUCCESS),
    failure: take(actionTypes.SAVE_ACTION_REQUEST_FAIL),
  });

  if (success) {
    const updatedAction = yield select(selectors.getAction);
    savedActionEvent(action, actionId, updatedAction);
    history.goBack();
  }
}

/**
 * Create a new action in Jira. Only exposed for tests, this generator is used
 * internally.
 * @param {Object} action A serialised action object
 * @param {Array<number>} equipList A list of equipRule ids for associating post action creation
 */
export function* addAction(action, equipList) {
  const token = yield select(selectors.getUserToken);

  yield put(addActionRequestChain(action, token));
  const { actionCreated } = yield race({
    actionCreated: take(actionTypes.ADD_ACTION_REQUEST_SUCCESS),
    failure: take(actionTypes.ADD_ACTION_REQUEST_FAIL),
  });

  if (actionCreated && equipList.length > 0) {
    yield put(
      postAssociateEquipRule({
        equipRuleIds: equipList,
        issueKey: actionCreated.result.issueKey,
        token,
      })
    );

    // NOTE: Wait for the association to succeed. The error for this is
    // "handled" in the associate saga (TODO: it's just thrown)
    yield take(ASSOCIATE_RULE_EQUIP_REQUEST_SUCCESS);
  }

  if (actionCreated) {
    addedActionEvent(actionCreated.result.issueKey, action);
    history.goBack();
  }
}

/**
 * The saga a user will follow to create a new action in the jira backend
 */
export function* addActionFlow() {
  while (true) {
    const { action, equipList = [] } = yield take(actionTypes.ADD_ACTION);

    // Serialise the action into a format the create API can consume
    const serialisedAction = serialiseCreateAction(action);

    // compute the upload id from the serialised action
    // NOTE: This is the same behaviour to support the captain API
    const uploadId = `${serialisedAction.projectKey}-${serialisedAction.issueType}`;

    // If there is no screenshot, persist use the existing serialised action
    if (action.screenshot === null) {
      yield fork(addAction, serialisedAction, equipList);
    } else {
      // Fork and watch for the completion of the upload
      yield fork(uploadScreenshot, uploadId, action.screenshot);
      const { uploadSuccess } = yield race({
        uploadSuccess: take(actionTypes.ATTACHMENT_UPLOAD_SUCCESS),
        uploadFailure: take(actionTypes.ATTACHMENT_UPLOAD_FAIL),
      });

      // If the upload was a success, update the screenshotUrl with the public
      // url and persist it to Jira
      if (uploadSuccess) {
        serialisedAction.screenshotUrl = uploadSuccess.url;
        yield fork(addAction, serialisedAction, equipList);
      }
    }
  }
}

/**
 * The saga in which the user will "update" or "save" an action to the Jira
 * backend
 */
export function* saveActionFlow() {
  while (true) {
    const { action, equipList = [] } = yield take(actionTypes.SAVE_ACTION);
    const uploadId = yield select(selectors.getActionId);
    const remoteAction = yield select(selectors.getAction);

    // Serialise the action into a format the edit API can consume
    const serialisedAction = serialiseEditAction(action);

    // If a transition reason is selected, we need to add some extra properties
    if (remoteAction.status !== action.status) {
      const userName = yield select(selectors.getUsername);
      const transitionText = [
        `~Transition: Transitioned from ${remoteAction.status} to ${action.status}.~`,
        `??${userName}??`,
      ].join("\n");

      serialisedAction.taggingList = [];
      serialisedAction.comment = transitionText;

      if (action.transitionReason !== "") {
        serialisedAction.transition_reason = {
          label: action.transitionReason,
          value: action.transitionReason,
        };
      }
    }

    if (
      equipList.length > 0 &&
      (action.status === "Closed" || action.status === "Rejected")
    ) {
      const equips = formatEquipList(equipList);
      const equipRuleIds = equipList.map((equip) => equip.id);
      const descriptionWithEquips = `${serialisedAction.description}\n\n\n**EQUIPS**\n${equips}`;

      serialisedAction.description = descriptionWithEquips;
      // Tell synco to unassociate the equips from this action
      // and wait for request to finish
      yield call(unassociateEquipRuleFlow, equipRuleIds);
    }

    // If there is no screenshot, persist use the existing serialised action
    if (action.screenshot === null) {
      yield fork(saveAction, serialisedAction);
    } else {
      // Fork and watch for the completion of the upload
      yield fork(uploadScreenshot, uploadId, action.screenshot);
      const { uploadSuccess } = yield race({
        uploadSuccess: take(actionTypes.ATTACHMENT_UPLOAD_SUCCESS),
        uploadFailure: take(actionTypes.ATTACHMENT_UPLOAD_FAIL),
      });

      // If the upload was a success, update the screenshot link with the public
      // url and persist it to Jira
      if (uploadSuccess) {
        serialisedAction.screenshot_link = uploadSuccess.url;
        yield fork(saveAction, serialisedAction);
      }
    }
  }
}
